import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";
import { useParams } from "react-router";
import twitchLogo from "@/assets/logos/twitch-logo-white.png";
import ytLogo from "@/assets/logos/yt_icon_mono_dark.png";

function formatDate(d) {
  const date = d ?? new Date();
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
}

export default function Page() {
  const params = useParams();
  const date = params.date ?? formatDate();
  const [hidePast, setHidePast] = useState(false);
  const { data, isPending, error } = useQuery({
    queryKey: ["schedule", date],
    queryFn: async () => {
      const response = await fetch(
        `${window.location.hostname === "localhost" ? "https://playlive.dev.experience.stjude.org" : ""}/static/schedule/${date}.json`,
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    },
  });

  const getKey = (item, index) => `schedule-item-${item.campaign.id}-${index}`;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">Schedule for {date}</h1>
      <div className="fieldset">
        <label className="label cursor-pointer text-base-content">
          Hide past streams
          <input
            type="checkbox"
            className="toggle"
            checked={hidePast}
            onChange={(e) => {
              setHidePast(e.target.checked);
            }}
          />
        </label>
      </div>
      {isPending && <p>Loading...</p>}
      {error && <p>Error: {error.message}</p>}
      {data && (
        <table className="table">
          <thead>
            <tr>
              <th>Start time</th>
              <th>End time</th>
              <th>Campaign</th>
            </tr>
          </thead>
          <tbody>
            {data.sort(sortSchedule).map((item, i) => {
              if (hidePast && Date.parse(item.schedule.ends_at) < Date.now()) {
                return null;
              }
              return (
                <ScheduleEntry
                  key={getKey(item, i)}
                  value={item}
                  listedDate={date.split("-").map((d) => Number(d))}
                />
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function sortSchedule(a, b) {
  const a_start = Date.parse(a.schedule.starts_at);
  const b_start = Date.parse(b.schedule.starts_at);

  if (a_start < b_start) {
    return -1;
  }
  if (a_start > b_start) {
    return 1;
  }
  const a_end = Date.parse(a.schedule.ends_at);
  const b_end = Date.parse(b.schedule.ends_at);
  if (a_end < b_end) {
    return -1;
  }
  if (a_end > b_end) {
    return 1;
  }
  return 0;
}

/**
 *
 * @param {{value:any,listedDate:number[]}} param0
 * @returns
 */
function ScheduleEntry({ value, listedDate }) {
  const startTime = formatTime(value.schedule.starts_at, listedDate); //`<t:${Math.floor(Date.parse(value.schedule.starts_at)/1000)}:t>`//DateTime.fromMillis(value.schedule.startsAt).setZone("America/Los_Angeles").toLocaleString(DateTime.TIME_SIMPLE);
  let endTime = "TBD";
  if (value.schedule.ends_at !== null) {
    endTime = formatTime(value.schedule.ends_at, listedDate); //`<t:${Math.floor(Date.parse(value.schedule.ends_at)/1000)}:t>`//DateTime.fromMillis(value.schedule.endsAt).setZone("America/Los_Angeles").toLocaleString(DateTime.TIME_SIMPLE);
  }

  const ld = getLivestreamData(value);

  return (
    <tr className={clsx([Date.parse(value.schedule.ends_at) < Date.now() && "opacity-50"])}>
      <td className={"w-fit"}>
        <div className={"flex flex-col text-center"}>
          <span className="text-sm text-base-content">{startTime[0]}</span>
          {startTime[1] && <span className="text-xs text-base-content">{startTime[1]}</span>}
        </div>
      </td>
      <td className={"w-fit"}>
        <div className={"flex flex-col text-center"}>
          <span className="text-sm text-base-content">{endTime[0]}</span>
          {endTime[1] && <span className="text-xs text-base-content">{endTime[1]}</span>}
        </div>
      </td>
      <td className={"min-w-fit"}>
        <a
          className={"btn flex flex-row flex-nowrap "}
          style={ld.style}
          href={ld.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {ld.logo && <img src={ld.logo} alt="Logo" className="h-6 mr-2" />}
          <span>{ld.name}</span>
        </a>
      </td>
      <td>{value.campaign.name}</td>
    </tr>
  );
}

function formatTime(dateTime, listedDate) {
  const startTime = new Date(Date.parse(dateTime)); //`<t:${Math.floor(Date.parse(dateTime)/1000)}:t>`//DateTime.fromMillis(value.schedule.startsAt).setZone("America/Los_Angeles").toLocaleString(DateTime.TIME_SIMPLE);
  let date = null;
  if (
    startTime.getDate() !== listedDate[2] ||
    startTime.getMonth() + 1 !== listedDate[1] ||
    startTime.getFullYear() !== listedDate[0]
  ) {
    date = startTime.toLocaleDateString();
  }
  return [startTime.toLocaleTimeString(), date];
}

function getLivestreamData(value) {
  let url = value.campaign.donate_url;
  let logo = null;
  let style = {};
  const name = value.campaign.user.username;
  //set the URL based on these priorities:  get from the livestream data block first, then from the user social data, then from the campaign data.
  if (value.campaign.livestream?.type === "twitch") {
    url = `https://www.twitch.tv/${value.campaign.livestream.channel.trim()}`;
    logo = twitchLogo;
    style = { backgroundColor: "#9146FF", color: "#fff" };
  } else if (value.campaign.livestream?.type === "youtube_live") {
    url = `https://www.youtube.com/channel/${value.campaign.livestream.channel.trim()}/live`;
    logo = ytLogo;
    style = { backgroundColor: "#FF0000", color: "#fff" };
  } else if (value.campaign.user.social.twitch) {
    url = `https://www.twitch.tv/${value.campaign.user.social.twitch.trim()}`;
    logo = twitchLogo;
    style = { backgroundColor: "#9146FF", color: "#fff" };
  } else if (value.campaign.user.social.youtube) {
    url = `https://www.youtube.com/channel/${value.campaign.user.social.youtube.trim()}/live`;
    logo = ytLogo;
    style = { backgroundColor: "#FF0000", color: "#fff" };
  } else if (value.campaign.user.social.facebook) {
    url = `https://www.facebook.com/${value.campaign.user.social.facebook.trim()}`;
  }
  return { url, logo, style, name };
}
